import MediaPlayback from '@optools/components/media/media-playback';
import { MediaElementProvider } from '@optools/components/media/MediaElementProvider/media-element-provider';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@optools/components/ui/sheet';
import { BtcGameAsset } from '@optools/services/operator-gateway/btc/types';
import {
  MediaType,
  PipelineStatus,
  StreamState,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { RowSelectionState } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { generateAssetKey } from '../utils/assets';
import { PipelineAssets } from './pipeline-assets';
import { PlaybackVerificationMediaControls } from './playback-verification-media-controls';

export interface PlaybackVerificationSheetProps {
  assets: BtcGameAsset[];
  gamePk: number;
  mediaId: string;
  mediaType: MediaType;
  pipelineStatus: PipelineStatus;
  streamState: StreamState;
}

export function PlaybackVerificationSheet({
  assets,
  gamePk,
  mediaId,
  mediaType,
  pipelineStatus,
  streamState,
}: PlaybackVerificationSheetProps) {
  const [open, setOpen] = useState(false);
  const [daiEnabled, setDaiEnabled] = useState<boolean>(false);

  const { asset: assetParam } = useSearch({
    from: '/_auth/btc-tools/schedule/$gamePk/pipelines/$pipelineId',
  });

  const navigate = useNavigate({ from: '/btc-tools/schedule/$gamePk/pipelines/$pipelineId' });

  const assetIndex = assets.findIndex((asset) => {
    if (!assetParam) return false;
    const [id, cdn] = assetParam.split('-');
    return `${asset.assetId}` === id && asset.cdn === cdn;
  });
  const currentAsset = assets[assetIndex];

  useEffect(() => {
    setDaiEnabled(currentAsset?.playback?.adsEnabled ?? false);
  }, [currentAsset?.playback?.adsEnabled]);

  useEffect(() => {
    if (currentAsset) setOpen(true);
  }, [currentAsset]);

  const handleRowSelection = (rowIndex: number) => {
    const asset = assets[rowIndex];
    if (asset) {
      navigate({
        replace: true,
        resetScroll: false,
        search: (params) => ({ ...params, asset: generateAssetKey(asset), mediaId }),
      });
    }
  };

  const handleCloseAutoFocus = () => {
    navigate({
      replace: true,
      resetScroll: false,
      search: (params) => ({ ...params, asset: '', mediaId: '' }),
    });
  };

  const selectedRow = useMemo<RowSelectionState>(() => ({ [assetIndex]: true }), [assetIndex]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent onCloseAutoFocus={handleCloseAutoFocus}>
        <SheetHeader>
          <SheetTitle>Asset ID: {currentAsset?.assetId}</SheetTitle>
          <SheetDescription hidden={true}>check asset playback</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col items-center justify-center gap-8 pt-10">
          <MediaElementProvider>
            {currentAsset?.playback ? (
              <div className="flex w-full flex-col lg:w-3/4">
                <MediaPlayback
                  autoPlay={true}
                  controls={true}
                  daiMode={daiEnabled}
                  isStayLive={false}
                  mediaType={mediaType}
                  muted={mediaType === MediaType.VIDEO}
                  playback={currentAsset.playback}
                />
                <PlaybackVerificationMediaControls
                  daiEnabled={daiEnabled}
                  daiSupported={currentAsset.playback.adsEnabled && mediaType === MediaType.VIDEO}
                  setDaiEnabled={setDaiEnabled}
                />
              </div>
            ) : (
              <p>Could Not Load Asset</p>
            )}
          </MediaElementProvider>

          <PipelineAssets
            assets={assets}
            enableRowSelection
            gamePk={gamePk}
            handleRowClick={handleRowSelection}
            mediaId={mediaId}
            pipelineStatus={pipelineStatus}
            rowSelection={selectedRow}
            streamState={streamState}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
